import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@firecms/ui";
import { useDataSource } from "@firecms/core";

import { fetchUserDisplayNames } from "../firebase/fetchUserDisplayNames";
import { useContentInstalls } from "../hooks/useContentInstalls";
import type { ContentInstallStatus } from "../types/contentInstalls";
import type { EventCollectionId } from "../workspace/collectionIds";
import SearchBar from "./SearchBar";
import { useAdminTranslation } from "../i18n/useAdminTranslation";
import {
  statsMuted,
  statsPageTitle,
  statsSectionTitle,
} from "./statsViewTheme";

type ContentInstallsViewProps = {
  eventId: EventCollectionId;
};

const PAGE_SIZE = 50;

const statusClass: Record<ContentInstallStatus, string> = {
  up_to_date: "bg-emerald-500 text-white",
  outdated: "bg-amber-500 text-white",
  not_installed: "bg-surface-400 text-white dark:bg-surface-600",
};

export function ContentInstallsView({ eventId }: ContentInstallsViewProps) {
  const { t } = useAdminTranslation();
  const dataSource = useDataSource();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [displayNames, setDisplayNames] = useState<Map<string, string>>(
    () => new Map()
  );
  const { rows, summary, loading } = useContentInstalls(eventId);

  useEffect(() => {
    setPage(0);
    setDisplayNames(new Map());
  }, [eventId]);

  useEffect(() => {
    setPage(0);
  }, [search]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((row) => {
      const name = displayNames.get(row.uid);
      return (
        row.label.toLowerCase().includes(q) ||
        row.uid.toLowerCase().includes(q) ||
        (name != null && name.toLowerCase().includes(q))
      );
    });
  }, [rows, search, displayNames]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = filteredRows.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE
  );

  const pageUidsKey = pageRows.map((row) => row.uid).join(",");

  useEffect(() => {
    if (!pageUidsKey) return;
    const uids = pageUidsKey.split(",").filter(Boolean);
    let cancelled = false;
    void fetchUserDisplayNames(dataSource, uids).then((names) => {
      if (cancelled || names.size === 0) return;
      setDisplayNames((prev) => {
        const next = new Map(prev);
        for (const [uid, name] of names) {
          next.set(uid, name);
        }
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [dataSource, pageUidsKey]);

  const statusLabel = (status: ContentInstallStatus): string => {
    if (status === "up_to_date") return t("contentInstalls.statusUpToDate");
    if (status === "outdated") return t("contentInstalls.statusOutdated");
    return t("contentInstalls.statusNotInstalled");
  };

  return (
    <div className="flex h-full w-full bg-surface-50 dark:bg-surface-900">
      <div className="mx-auto w-full max-w-6xl px-4 py-8">
        <h1 className={statsPageTitle}>{t("contentInstalls.viewTitle")}</h1>
        <p className={`mt-2 text-sm ${statsMuted}`}>
          {t("contentInstalls.viewDescription")}
        </p>

        {loading ? (
          <div className="mt-8 flex justify-center">
            <CircularProgress size="medium" />
          </div>
        ) : (
          <>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <SummaryCard
                label={t("contentInstalls.publishedVersion")}
                value={
                  summary.publishedVersion != null
                    ? `v${summary.publishedVersion}`
                    : "—"
                }
              />
              <SummaryCard
                label={t("contentInstalls.upToDate")}
                value={`${summary.upToDate}/${summary.totalParticipants}`}
              />
              <SummaryCard
                label={t("contentInstalls.outdated")}
                value={`${summary.outdated}`}
              />
              <SummaryCard
                label={t("contentInstalls.notInstalled")}
                value={`${summary.notInstalled}`}
              />
            </div>

            <div className="mt-6">
              <SearchBar
                onSearch={setSearch}
                placeholder={t("contentInstalls.searchPlaceholder")}
              />
            </div>

            {filteredRows.length === 0 ? (
              <p className={`mt-6 text-sm ${statsMuted}`}>
                {t("contentInstalls.empty")}
              </p>
            ) : (
              <>
                <div className="mt-4 overflow-x-auto rounded-lg border border-surface-200 dark:border-surface-700">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-surface-100 dark:bg-surface-800">
                      <tr>
                        <th className="px-3 py-2 font-medium">
                          {t("contentInstalls.colParticipant")}
                        </th>
                        <th className="px-3 py-2 font-medium">
                          {t("contentInstalls.colVersion")}
                        </th>
                        <th className="px-3 py-2 font-medium">
                          {t("contentInstalls.colInstalledAt")}
                        </th>
                        <th className="px-3 py-2 font-medium">
                          {t("contentInstalls.colStatus")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.map((row) => (
                        <tr
                          key={row.uid}
                          className="border-t border-surface-200 dark:border-surface-700"
                        >
                          <td className="px-3 py-2">
                            <div className="font-medium">
                              {displayNames.get(row.uid) ?? row.label}
                            </div>
                            <div className={`text-xs ${statsMuted}`}>
                              {row.uid}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {row.installedContentVersion != null
                              ? `v${row.installedContentVersion}`
                              : "—"}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {row.installedContentAt
                              ? row.installedContentAt.toLocaleString()
                              : "—"}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex rounded-full px-2 py-1 text-xs ${statusClass[row.status]}`}
                            >
                              {statusLabel(row.status)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {pageCount > 1 && (
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <p className={`text-sm ${statsMuted}`}>
                      {t("contentInstalls.pageStatus", {
                        page: safePage + 1,
                        pageCount,
                        total: filteredRows.length,
                      })}
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="rounded-md border border-surface-300 px-3 py-1.5 text-sm disabled:opacity-40 dark:border-surface-600"
                        disabled={safePage <= 0}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                      >
                        {t("contentInstalls.prevPage")}
                      </button>
                      <button
                        type="button"
                        className="rounded-md border border-surface-300 px-3 py-1.5 text-sm disabled:opacity-40 dark:border-surface-600"
                        disabled={safePage >= pageCount - 1}
                        onClick={() =>
                          setPage((p) => Math.min(pageCount - 1, p + 1))
                        }
                      >
                        {t("contentInstalls.nextPage")}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-surface-200 bg-white px-4 py-3 dark:border-surface-700 dark:bg-surface-800">
      <div className={`text-xs uppercase tracking-wide ${statsMuted}`}>
        {label}
      </div>
      <div className={`mt-1 ${statsSectionTitle}`}>{value}</div>
    </div>
  );
}
